import React, { useMemo, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, X, Loader2, Download, ThumbsUp, ThumbsDown, Scissors, Film, Eye, Video, Play } from "lucide-react";
import { MyClipsByProjectIdQuery } from "@/generated/graphql";
import { useMutation } from "@apollo/client";
import { RATE_CLIP } from "@/queries/queries";
import { useClipPolling } from "@/hooks/use-polling";
import { useToast } from "@/hooks/use-toast";
import { getClipStatusText } from "@/lib/get-clip-status-text";
import { formatDuration } from "@/lib/format";
import { downloadClipVideo } from "@/lib/downloads/clip-download";
import ProjectPreview, { ProjectPreviewRef } from "@/components/ProjectPreview";
import ShareClipButton from "@/components/ShareClipButton";
import ClipVersionBadge from "@/components/ClipVersionBadge";
import { ClipPublishingControls } from "@/components/publishing/ClipPublishingControls";
import { EditWithAiButton } from "@/components/assistant/EditWithAiButton";
import { canMakeQuoteCarousel } from "@/lib/quoteCarousel/canMakeQuoteCarousel";
import { PublishStatusOverlay } from "@/components/publishing/PublishStatusOverlay";
import type { PublishMediaKinds, PublishUiStatus } from "@/lib/publishing/types";
import type { VersionGroup } from "@/hooks/use-clip-versions";
import type { PostLayoutConfig } from "@/lib/layouts/types";
import { buildClipTranscriptionSegments } from "@/lib/preview/buildClipTranscriptionSegments";
import {
  resolveClipCardPreview,
  type ResolvedPersistedClipPreview,
} from "@/lib/preview/resolvePersistedClipPreview";
import { useDownloadUpsell } from "@/contexts/downloadUpsellContext";
import {
  resolveClipCardStillUrl,
  shouldMountClipCardLivePreview,
} from "@/lib/clips/resolveClipCardPreview";
import { startClipCardPlayback } from "@/lib/clips/startClipCardPlayback";
import { resolveCropKeyframeAtTime } from "@/lib/crop/resolveCropKeyframeAtTime";

interface LogoConfig {
  url?: string;
  position?: string;
  size?: number;
  opacity?: number;
  horizontalMargin?: number;
  verticalMargin?: number;
}

export interface VerticalClipCardProps {
  clip: MyClipsByProjectIdQuery['myClips'][0];
  clipNumber: number;
  isSelected: boolean;
  onToggleSelection: (clipId: string) => void;
  onEditVideo?: (clipId: string) => void;
  /** Opens the clip in AI Studio (shown only when the page passes it). */
  onEditWithAi?: (clipId: string) => void;
  onExport?: (clipId: string) => void;
  isExporting?: boolean;
  onPlay?: (clipId: string, pause: () => void) => void;
  /** When provided by the page, this card may hold a decoder only while true. */
  isPreviewActive?: boolean;
  onPreviewPlay?: (clipId: string) => void;
  onPreviewPause?: (clipId: string) => void;
  videoUrl?: string;
  cropCenterKeyframes?: Array<{ time: number; endTime: number; x: number; y: number }>;
  logoConfig?: LogoConfig;
  subtitlesEnabled?: boolean;
  subtitleConfig?: Record<string, any>;
  transcriptionWords?: any[];
  projectDuration?: number;
  speakerLayout?: {
    numSpeakers: number;
    speakers: Array<{
      face_id: string;
      scenes: Array<{ start: number; end: number; cropCenter: { x: number; y: number } }>;
    }>;
    sceneTimeline?: Array<{ start: number; end: number; faceIds: string[] }>;
  };
  postLayout?: PostLayoutConfig | null;
  showWatermark?: boolean;
  projectDocumentId?: string;
  previewProcessing?: ResolvedPersistedClipPreview;
  versionGroup?: VersionGroup<MyClipsByProjectIdQuery['myClips'][0]>;
  onSendToQueue?: (clipId: string) => void;
  onSendToTikTok?: (clipId: string) => void;
  onPublishToYouTube?: (clipId: string) => void;
  onPublishToInstagram?: (clipId: string) => void;
  onPublishToFacebook?: (clipId: string) => void;
  tiktokPublishStatus?: PublishUiStatus;
  tiktokPublishFailReason?: string | null;
  youtubePublishStatus?: PublishUiStatus;
  youtubePublishFailReason?: string | null;
  instagramPublishStatus?: PublishUiStatus;
  instagramPublishFailReason?: string | null;
  facebookPublishStatus?: PublishUiStatus;
  facebookPublishFailReason?: string | null;
  /** Latest publish media_kind per platform ('carousel' adds an icon). */
  publishMediaKinds?: PublishMediaKinds;
}

const PENDING_STATUSES = ['processing', 'pre-queued', 'queued'];

export const VerticalClipCard: React.FC<VerticalClipCardProps> = ({
  clip,
  clipNumber,
  isSelected,
  onToggleSelection,
  onEditVideo,
  onEditWithAi,
  onExport,
  isExporting,
  onPlay,
  isPreviewActive,
  onPreviewPlay,
  onPreviewPause,
  videoUrl,
  cropCenterKeyframes,
  logoConfig,
  subtitlesEnabled,
  subtitleConfig,
  transcriptionWords,
  projectDuration,
  speakerLayout,
  postLayout,
  showWatermark,
  projectDocumentId,
  previewProcessing,
  versionGroup,
  onSendToQueue,
  onSendToTikTok,
  onPublishToYouTube,
  onPublishToInstagram,
  onPublishToFacebook,
  tiktokPublishStatus,
  tiktokPublishFailReason,
  youtubePublishStatus,
  youtubePublishFailReason,
  instagramPublishStatus,
  instagramPublishFailReason,
  facebookPublishStatus,
  facebookPublishFailReason,
  publishMediaKinds,
}) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { notifyClipDownloaded } = useDownloadUpsell();
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewRef = useRef<ProjectPreviewRef>(null);
  const lastReportedLivePreviewPlayStateRef = useRef(false);
  const suppressPauseReleaseRef = useRef(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [localPlayerMounted, setLocalPlayerMounted] = useState(false);
  const isPlayerMounted = isPreviewActive ?? localPlayerMounted;
  const [isLivePreviewPlaying, setIsLivePreviewPlaying] = useState(false);
  const [clipRating, setClipRating] = useState<string>(clip.curation_rating || 'neutral');
  const [rateClipMutation] = useMutation(RATE_CLIP);
  // Use polling hook to get real-time updates when clip is processing
  const { data: polledClip } = useClipPolling(
    clip.id,
    {
      enabled: (
        PENDING_STATUSES.includes(clip.clipping_status) ||
        PENDING_STATUSES.includes(clip.preparing_status) ||
        PENDING_STATUSES.includes(clip.rendering_status)
      ),
      interval: 5000,
    }
  );

  // Use polled data if available, otherwise fall back to props
  const currentClip = polledClip || clip;

  // Determine current status based on polled data or props
  const currentStatusState = useMemo(() => {
    const activeClip = polledClip || clip;
    if (activeClip.rendering_status !== 'skipped') {
      return activeClip.rendering_status;
    }
    if (activeClip.preparing_status !== 'skipped') {
      return activeClip.preparing_status;
    }
    return activeClip.clipping_status;
  }, [clip, polledClip]);

  const effectiveStatusLabel = getClipStatusText(currentClip as any);

  // Use the saved multi-segment composition; never fall back to segments[0] alone.
  const {
    startTime,
    endTime,
    intervals: previewIntervals,
    cropCenterKeyframes: resolvedCropCenterKeyframes,
    transitions,
    videoEffects,
    keyframeTransitions,
    layers,
  } = useMemo(() => resolveClipCardPreview({
    clip,
    previewProcessing,
    fallbackCropCenterKeyframes: cropCenterKeyframes,
    projectDuration,
  }), [clip, previewProcessing, cropCenterKeyframes, projectDuration]);
  const effectiveCropCenterKeyframes = resolvedCropCenterKeyframes;
  const hasPreviewTimeRange = endTime > startTime;
  const previewLayoutKey = postLayout
    ? `post-${postLayout.kind}`
    : `speaker-${speakerLayout?.numSpeakers ?? 1}`;

  // Build transcriptionSegments from library transcription words, filtered by clip time range
  const transcriptionSegments = useMemo(() => {
    if (!subtitlesEnabled || !subtitleConfig || !transcriptionWords || transcriptionWords.length === 0 || !hasPreviewTimeRange) return undefined;
    const words = transcriptionWords.map((w: any) => ({
      word: w.word || w.punctuated_word || w.text || '',
      start: w.start ?? w.startTime ?? w.start_time ?? 0,
      end: w.end ?? w.endTime ?? w.end_time ?? 0,
    }));
    const intervals = previewIntervals?.length
      ? previewIntervals
      : [{ startTime, endTime }];
    return buildClipTranscriptionSegments({
      words,
      intervals,
      projectDuration: projectDuration ?? Infinity,
    });
  }, [
    transcriptionWords,
    startTime,
    endTime,
    subtitlesEnabled,
    subtitleConfig,
    hasPreviewTimeRange,
    previewIntervals,
    projectDuration,
  ]);

  const releasePreview = useCallback(() => {
    previewRef.current?.pause();
    videoRef.current?.pause();
    setShowLivePreview(false);
    setIsLivePreviewPlaying(false);
    lastReportedLivePreviewPlayStateRef.current = false;
    setLocalPlayerMounted(false);
  }, []);

  const handlePlayStateChange = useCallback((isPlaying: boolean) => {
    if (isPlaying) {
      setIsLivePreviewPlaying(true);
      const wasPlaying = lastReportedLivePreviewPlayStateRef.current;
      lastReportedLivePreviewPlayStateRef.current = true;
      if (!wasPlaying) {
        onPlay?.(clip.id, releasePreview);
      }
      return;
    }

    lastReportedLivePreviewPlayStateRef.current = false;
    if (suppressPauseReleaseRef.current) {
      suppressPauseReleaseRef.current = false;
    }
  }, [clip.id, onPlay, releasePreview]);

  // Check if clip is expired
  const isExpired = useMemo(() => {
    return (
      currentClip.clipping_status === 'expired' ||
      currentClip.preparing_status === 'expired' ||
      currentClip.rendering_status === 'expired'
    );
  }, [currentClip.clipping_status, currentClip.preparing_status, currentClip.rendering_status]);

  const thumbnailUrl = resolveClipCardStillUrl(currentClip as any);

  // Compute crop position for thumbnail to match the preview framing.
  // Only needed when showing a source-video thumbnail (clip_thumbnail);
  // finished_video_thumbnail_url is already from the 9:16 render.
  // Crop x values are percentages (0-100) of the source video width.
  const thumbnailCropPosition = useMemo(() => {
    // Rendered thumbnail is already 9:16 — no crop needed
    if (currentClip.finished_video_thumbnail_url || currentClip.thumbnail_url) return undefined;

    // Use clip's own render crop data (clip.data.render.crop.scenes)
    const renderScenes = (currentClip as any).data?.render?.crop?.scenes;
    if (renderScenes && renderScenes.length > 0) {
      const cropX = renderScenes[0].cropCenter?.x;
      if (cropX !== undefined) return `${Math.max(0, Math.min(100, cropX))}% 50%`;
    }

    // Fall back to parent crop keyframes / speaker layout
    let cropX: number | undefined;
    if (speakerLayout && speakerLayout.speakers.length > 0) {
      const timeline = speakerLayout.sceneTimeline;
      if (timeline) {
        const activeScene = timeline.find(s => startTime >= s.start && startTime <= s.end);
        if (activeScene && activeScene.faceIds.length > 0) {
          const speaker = speakerLayout.speakers.find(s => s.face_id === activeScene.faceIds[0]);
          const scene = speaker?.scenes.find(s => startTime >= s.start && startTime <= s.end);
          if (scene) cropX = scene.cropCenter.x;
        }
      }
      if (cropX === undefined) {
        const first = speakerLayout.speakers[0]?.scenes?.[0]?.cropCenter;
        if (first) cropX = first.x;
      }
    }
    if (cropX === undefined && effectiveCropCenterKeyframes && effectiveCropCenterKeyframes.length > 0) {
      cropX = resolveCropKeyframeAtTime(effectiveCropCenterKeyframes, startTime)?.x;
    }
    if (cropX === undefined) return undefined;
    return `${Math.max(0, Math.min(100, cropX))}% 50%`;
  }, [currentClip, effectiveCropCenterKeyframes, speakerLayout, startTime]);

  const isCurrentStateSuccess = currentStatusState === 'success';
  const isCurrentStateFailure = currentStatusState === 'failure';
  const isCurrentStateExpired = currentStatusState === 'expired';
  const isCurrentStateSkipped = currentStatusState === 'skipped';
  const isCurrentStateProcessing = !isCurrentStateSuccess && !isCurrentStateFailure && !isCurrentStateExpired && !isCurrentStateSkipped;

  const effectiveDownloadUrl = currentClip.finished_video_url || currentClip.compressed_video_url || currentClip.clipped_video_url;
  const isRendered = currentClip.rendering_status === 'success' && !!effectiveDownloadUrl;
  const canShowPublishControls = isRendered && !isExpired;

  // Disable selection when clip is processing, failed, or expired
  const isSelectionDisabled = isCurrentStateProcessing || isCurrentStateFailure || isExpired;
  const canPlay = !isSelectionDisabled && ((videoUrl && hasPreviewTimeRange) || isRendered);

  const handleDownload = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!effectiveDownloadUrl || isDownloading) return;

    setIsDownloading(true);

    try {
      const displayName = currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber });
      await downloadClipVideo({
        clipId: currentClip.id,
        fallbackName: displayName,
        downloadUrl: effectiveDownloadUrl,
      });
      notifyClipDownloaded();
    } catch (error) {
      console.error('Download failed:', error);
      toast({ title: t('projectClips.toast.downloadError'), variant: "destructive" });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleRating = async (e: React.MouseEvent, rating: 'like' | 'dislike') => {
    e.stopPropagation();
    try {
      const newRating = clipRating === rating ? 'neutral' : rating;
      await rateClipMutation({ variables: { id: clip.id, rating: newRating } });
      setClipRating(newRating);
      toast({
        title: newRating === 'neutral' ? t('projectEditor.clipRatingRemoved') : t('projectEditor.clipRated'),
      });
    } catch (error) {
      console.error('Error rating clip:', error);
      toast({ title: t('projectEditor.clipRatingError'), variant: "destructive" });
    }
  };

  const statusChip = useMemo(() => {
    if (isCurrentStateProcessing) return { label: effectiveStatusLabel, className: 'bg-blue-500/15 text-blue-400 border-blue-500/20' };
    if (isCurrentStateFailure) return { label: effectiveStatusLabel, className: 'bg-red-500/15 text-red-400 border-red-500/20' };
    if (isCurrentStateExpired) return { label: effectiveStatusLabel, className: 'bg-amber-500/15 text-amber-400 border-amber-500/20' };
    if (isDownloading) return { label: t('verticalClipCard.downloadingVideo'), className: 'bg-primary/15 text-primary border-primary/20' };
    return null;
  }, [isCurrentStateProcessing, isCurrentStateFailure, isCurrentStateExpired, isDownloading, effectiveStatusLabel, t]);

  return (
    <>
      <Card
        key={clip.id}
        className={`min-w-0 transition-all duration-300 hover:shadow-lg hover:scale-[1.02] group ${
          isSelected ? 'ring-2 ring-primary shadow-[0_0_20px_-5px] shadow-primary/30' : ''
        } ${isSelectionDisabled ? 'opacity-60' : ''} ${!isSelectionDisabled ? 'cursor-pointer' : 'cursor-not-allowed'}`}
        onClick={() => !isSelectionDisabled && onToggleSelection(clip.id)}
      >
        <div className="relative aspect-[9/16] bg-black overflow-hidden rounded-t-lg">
        {/* Idle cards show a still. Live Remotion mounts only after play —
            one 1080×1920 decoder per card is what jetsams iOS Safari. */}
        {shouldMountClipCardLivePreview({
          isPlayerMounted,
          hasSourceVideo: !!videoUrl,
          hasPreviewTimeRange,
          isRendered,
          showLivePreview,
        }) ? (
          <div className={`w-full h-full ${isPlayerMounted ? 'pointer-events-auto' : 'pointer-events-none'}`}>
            <ProjectPreview
              key={previewLayoutKey}
              ref={previewRef}
              videoUrl={videoUrl}
              startTime={startTime}
              endTime={endTime}
              intervals={previewIntervals}
              width={1080}
              height={1920}
              autoPlay={isLivePreviewPlaying}
              loop={true}
              showVolumeControls={isPlayerMounted}
              cropCenterKeyframes={
                postLayout || !speakerLayout
                  ? effectiveCropCenterKeyframes
                  : undefined
              }
              onPlayStateChange={handlePlayStateChange}
              logoConfig={logoConfig}
              subtitlesEnabled={subtitlesEnabled && !!transcriptionSegments}
              transcriptionSegments={transcriptionSegments}
              subtitleConfig={subtitleConfig}
              speakerLayout={postLayout ? undefined : speakerLayout}
              postLayout={postLayout}
              videoEffects={videoEffects}
              transitions={transitions}
              keyframeTransitions={keyframeTransitions}
              layers={layers}
              showWatermark={showWatermark}
              projectDocumentId={projectDocumentId ?? (clip as any).project_id ?? undefined}
              libraryDocumentId={(clip as any).library_id || undefined}
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        ) : isPlayerMounted && isRendered ? (
          <video
            ref={(el) => {
              (videoRef as React.MutableRefObject<HTMLVideoElement | null>).current = el;
              if (el) {
                el.volume = 0.5;
                el.play().catch(() => {});
              }
            }}
            src={effectiveDownloadUrl}
            controls
            playsInline
            className="w-full h-full object-contain"
            onPlay={() => {
              if (onPlay && videoRef.current) {
                onPlay(clip.id, releasePreview);
              }
            }}
            onPause={() => {
              if (suppressPauseReleaseRef.current) {
                suppressPauseReleaseRef.current = false;
                return;
              }
              releasePreview();
              onPreviewPause?.(clip.id);
            }}
          />
        ) : thumbnailUrl ? (
          <img
            src={thumbnailUrl}
            alt={currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
            className={`w-full h-full ${thumbnailCropPosition ? 'object-cover' : 'object-contain'}`}
            style={thumbnailCropPosition ? { objectPosition: thumbnailCropPosition } : undefined}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-muted">
            <span className="text-muted-foreground text-sm">{t('verticalClipCard.noPreview')}</span>
          </div>
        )}

        {/* Play button - activates the player on click */}
        {!isPlayerMounted && canPlay && (
          <button
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[5] w-11 h-11 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/15 transition-all duration-200 hover:bg-primary/80 hover:scale-110 hover:border-primary/40 shadow-[0_2px_16px_rgba(0,0,0,0.4)]"
            onClick={(e) => {
              e.stopPropagation();
              startClipCardPlayback(
                () => {
                  onPreviewPlay?.(clip.id);
                  setLocalPlayerMounted(true);
                  if (!isRendered) {
                    setIsLivePreviewPlaying(true);
                  }
                },
                () => {
                  if (!isRendered) {
                    previewRef.current?.play();
                  }
                },
              );
            }}
          >
            <Play className="w-5 h-5 text-white fill-white ml-0.5" />
          </button>
        )}

        {/* Duration Badge — glass pill */}
        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-sm text-white text-[11px] font-medium px-2 py-0.5 rounded-full tracking-wide pointer-events-none">
          {formatDuration(currentClip.duration_seconds)}
        </div>

        {/* Toggle between live preview and rendered video */}
        {isPlayerMounted && isRendered && videoUrl && hasPreviewTimeRange && (
          <button
            className="absolute top-8 right-2 z-10 bg-black/60 backdrop-blur-sm text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-black/80 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              const nextShowLivePreview = !showLivePreview;
              if (nextShowLivePreview) {
                suppressPauseReleaseRef.current = true;
                startClipCardPlayback(
                  () => {
                    setShowLivePreview(true);
                    setIsLivePreviewPlaying(true);
                  },
                  () => previewRef.current?.play(),
                );
                return;
              }
              suppressPauseReleaseRef.current = true;
              setShowLivePreview(false);
              setIsLivePreviewPlaying(false);
              lastReportedLivePreviewPlayStateRef.current = false;
            }}
            title={showLivePreview ? t('verticalClipCard.showRendered') : t('verticalClipCard.showPreview')}
          >
            {showLivePreview ? <Video className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}

        {/* Selection Checkbox */}
        {!isSelectionDisabled && (
          <div
            className="absolute top-1 left-1 z-10"
            onClick={(e) => { e.stopPropagation(); onToggleSelection(clip.id); }}
          >
            <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-primary border-primary text-primary-foreground' : 'bg-background/80 border-muted-foreground/40 hover:border-primary'}`}>
              {isSelected && (
                <Check className="w-3 h-3" />
              )}
            </div>
          </div>
        )}
        {canShowPublishControls && (
        <PublishStatusOverlay
          className={`absolute z-20 ${isSelectionDisabled ? 'top-2 left-2' : 'top-1 left-8'}`}
          statuses={[
            {
              platform: 'tiktok',
              status: tiktokPublishStatus,
              failReason: tiktokPublishFailReason,
              mediaKind: publishMediaKinds?.tiktok,
            },
            {
              platform: 'instagram',
              status: instagramPublishStatus,
              failReason: instagramPublishFailReason,
              mediaKind: publishMediaKinds?.instagram,
            },
            {
              platform: 'facebook',
              status: facebookPublishStatus,
              failReason: facebookPublishFailReason,
              mediaKind: publishMediaKinds?.facebook,
            },
            {
              platform: 'youtube',
              status: youtubePublishStatus,
              failReason: youtubePublishFailReason,
              mediaKind: publishMediaKinds?.youtube,
            },
          ]}
        />
        )}

        {/* Status Indicator Overlay */}
        {isCurrentStateProcessing && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300">
            <div className="text-center">
              <Loader2 className="w-8 h-8 animate-spin text-white mx-auto mb-2" />
              <span className="text-white text-xs">{effectiveStatusLabel}</span>
            </div>
          </div>
        )}

        {isExpired && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300">
            <div className="text-center">
              <X className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <span className="text-amber-500 text-xs">{effectiveStatusLabel}</span>
            </div>
          </div>
        )}

        {isCurrentStateFailure && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center transition-opacity duration-300">
            <div className="text-center">
              <X className="w-8 h-8 text-red-500 mx-auto mb-2" />
              <span className="text-red-500 text-xs">{effectiveStatusLabel}</span>
            </div>
          </div>
        )}

        {/* Rendered success indicator — bottom-left glass chip */}
        {isCurrentStateSuccess && !isCurrentStateProcessing && (
          <div className="absolute bottom-2 left-2 bg-emerald-500/20 backdrop-blur-sm text-emerald-400 text-[10px] font-semibold px-2 py-0.5 rounded-full flex items-center gap-1 pointer-events-none border border-emerald-500/20">
            <Check className="w-3 h-3" />
            {t('verticalClipCard.ready')}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="min-w-0 p-2.5 space-y-2">
        {/* Title + Version badge */}
        <div className="flex items-center gap-1.5 min-w-0">
          <h3 className="font-semibold text-sm leading-tight truncate flex-1" title={currentClip.name || undefined}>
            {currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
          </h3>
          <ClipVersionBadge
            versionGroup={versionGroup}
            currentClipId={currentClip.id}
            currentVersionNumber={currentClip.version_number}
          />
        </div>

        {/* Score bar + Rating row */}
        <div className="flex items-center gap-2">
          {currentClip.clip_score != null && (
            <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
              <span>Score:</span>
              <div className="w-8 h-1 bg-secondary rounded-full overflow-hidden">
                <div className="h-full bg-gradient-primary rounded-full" style={{ width: `${Math.round((currentClip.clip_score ?? 0) * 100)}%` }} />
              </div>
              <span className="font-medium">{Math.round((currentClip.clip_score ?? 0) * 100)}</span>
            </div>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <button
              className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${clipRating === 'like' ? 'bg-green-500 text-white' : 'bg-muted/60 text-muted-foreground hover:text-green-500'}`}
              onClick={(e) => handleRating(e, 'like')}
            >
              <ThumbsUp className="w-3 h-3" />
            </button>
            <button
              className={`w-6 h-6 rounded flex items-center justify-center transition-colors ${clipRating === 'dislike' ? 'bg-red-500 text-white' : 'bg-muted/60 text-muted-foreground hover:text-red-500'}`}
              onClick={(e) => handleRating(e, 'dislike')}
            >
              <ThumbsDown className="w-3 h-3" />
            </button>
          </div>
        </div>

        {/* Status chip — only when there's no overlay already showing it */}
        {statusChip && !isCurrentStateProcessing && (
          <span className={`inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full border ${statusChip.className}`}>
            {statusChip.label}
          </span>
        )}

        {/* Download + Share buttons */}
        {currentClip.finished_video_url && (
          <div className="flex gap-1.5">
            <Button
              variant="gradient"
              size="sm"
              onClick={handleDownload}
              disabled={isDownloading}
              className="flex-1 h-8 text-xs"
            >
              {isDownloading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Download className="w-3.5 h-3.5" />
              )}
              {t('verticalClipCard.download')}
            </Button>
            <ShareClipButton
              downloadUrl={effectiveDownloadUrl!}
              clipName={currentClip.name || t('verticalClipCard.clipNumber', { number: clipNumber })}
            />
          </div>
        )}

        {/* Action buttons row */}
        {(onEditVideo || onExport || onEditWithAi) && (
          <div className="flex gap-1.5">
            {onExport && !isCurrentStateProcessing && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 min-w-0 flex-auto gap-1 px-1.5 text-xs"
                disabled={isExporting}
                onClick={(e) => {
                  e.stopPropagation();
                  onExport(clip.id);
                }}
              >
                {isExporting ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Film className="w-3.5 h-3.5" />
                )}
                <span className="truncate">{t('projectClips.export')}</span>
              </Button>
            )}
            {onEditVideo && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 min-w-0 flex-auto gap-1 px-1.5 text-xs"
                disabled={isCurrentStateProcessing}
                onClick={(e) => {
                  e.stopPropagation();
                  onEditVideo(clip.id);
                }}
              >
                <Scissors className="w-3.5 h-3.5" />
                <span className="truncate">{t('projectClips.editVideo')}</span>
              </Button>
            )}
            {onEditWithAi && (
              <EditWithAiButton
                disabled={isCurrentStateProcessing}
                onClick={() => onEditWithAi(clip.id)}
              />
            )}
          </div>
        )}

        <ClipPublishingControls
          clipId={clip.id}
          canPublish={canShowPublishControls}
          canMakeCarousel={canMakeQuoteCarousel(currentClip)}
          compact
          onSendToQueue={onSendToQueue}
          onSendToTikTok={onSendToTikTok}
          onPublishToYouTube={onPublishToYouTube}
          onPublishToInstagram={onPublishToInstagram}
          onPublishToFacebook={onPublishToFacebook}
          tiktokPublishStatus={tiktokPublishStatus}
          tiktokPublishFailReason={tiktokPublishFailReason}
          youtubePublishStatus={youtubePublishStatus}
          youtubePublishFailReason={youtubePublishFailReason}
          instagramPublishStatus={instagramPublishStatus}
          instagramPublishFailReason={instagramPublishFailReason}
          facebookPublishStatus={facebookPublishStatus}
          facebookPublishFailReason={facebookPublishFailReason}
        />
      </div>
    </Card>

    </>
  );
};

export default VerticalClipCard;
